export const TEMPLATE_CONFIG = {
  1: { ratio: 4 / 3, width: 1200, height: 900 },
  2: { ratio: 390 / 611, width: 780, height: 1222 },
  3: { ratio: 619 / 486, width: 1238, height: 972 },
  4: { ratio: 7 / 1, width: 2100, height: 300 },
  5: { ratio: 7 / 2, width: 2100, height: 600 },
  6: { ratio: 988 / 487, width: 988, height: 487 },
} as const;

function isFullBleedTemplate(templateNum: number) {
  return templateNum === 4 || templateNum === 5;
}

export const BASE_ICONS = [
  { id: 'assist', labelKey: 'banners:v1.icons.assist', src: '/img/assist.png' },
  { id: 'food', labelKey: 'banners:v1.icons.food', src: '/img/food.png' },
  { id: 'breakfast', labelKey: 'banners:v1.icons.breakfast', src: '/img/breakfast.png' },
  { id: 'hotel', labelKey: 'banners:v1.icons.hotel', src: '/img/hotel.png' },
  { id: 'car', labelKey: 'banners:v1.icons.car', src: '/img/car.png' },
  { id: 'plane', labelKey: 'banners:v1.icons.plane', src: '/img/plane.png' },
  { id: 'excursion', labelKey: 'banners:v1.icons.excursion', src: '/img/excursion.png' },
  { id: 'drink', labelKey: 'banners:v1.icons.drink', src: '/img/drink.png' },
  { id: 'luggage', labelKey: 'banners:v1.icons.luggage', src: '/img/luggage.png' },
  { id: 'group', labelKey: 'banners:v1.icons.group', src: '/img/group.png' },
  { id: 'cruise', labelKey: 'banners:v1.icons.cruise', src: '/img/cruise.png' },
  { id: 'guide', labelKey: 'banners:v1.icons.guide', src: '/img/guide.png' },
];

export const ICONS = [
  ...BASE_ICONS.map((i) => ({ ...i, variant: 'solid' as const })),
  ...BASE_ICONS.map((i) => ({
    ...i,
    id: `${i.id}-line`,
    src: `/img/${i.id}-line.png`,
    variant: 'line' as const,
  })),
];

export const DEFAULT_TAG_BG_COLOR = '#2554c7';
export const DEFAULT_TAG_TEXT_COLOR = '#ffffff';
export const DEFAULT_ICON_COLOR = '#2554c7';

export function isV1Banner(data: { version?: number | string | null } | null | undefined) {
  return Number(data?.version) === 1;
}

export function isV1Published(
  data: { version?: number | string | null; publicado?: boolean | null } | null | undefined
) {
  return isV1Banner(data) && Boolean(data?.publicado);
}

export function normalizeIconos(value: unknown) {
  const raw = (() => {
    if (!value) return [] as string[];
    if (Array.isArray(value)) return value.filter(Boolean).map(String);
    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [] as string[];
  })();
  const seen = new Set<string>();
  return raw.reduce<string[]>((ids, id) => {
    let canonical = id === 'ticket' ? 'excursion' : id;
    if (canonical === 'ticket-line') canonical = 'excursion-line';
    if (seen.has(canonical)) return ids;
    seen.add(canonical);
    ids.push(canonical);
    return ids;
  }, []);
}

export function buildTagsFromApiFields({
  tag1,
  tag2,
  tag3,
  mostrarTag1,
  mostrarTag2,
  mostrarTag3,
}: {
  tag1?: string;
  tag2?: string;
  tag3?: string;
  mostrarTag1?: boolean;
  mostrarTag2?: boolean;
  mostrarTag3?: boolean;
} = {}) {
  return [
    { text: tag1 || '', hide: Boolean(mostrarTag1) },
    { text: tag2 || '', hide: Boolean(mostrarTag2) },
    { text: tag3 || '', hide: Boolean(mostrarTag3) },
  ];
}

/** Per-template character limits for banner card text fields (T1–T3, T6). */
export const TEMPLATE_FIELD_LIMITS: Record<1 | 2 | 3 | 6, Record<string, number>> = {
  1: {
    titulo: 74,
    tag1: 26,
    tag2: 26,
    tag3: 26,
    beneficio1: 36,
    beneficio2: 36,
    precio: 15,
    descuento: 26,
    extra: 26,
  },
  2: {
    titulo: 74,
    tag1: 20,
    tag2: 20,
    tag3: 26,
    precio: 15,
    descuento: 26,
    extra: 26,
  },
  3: {
    titulo: 74,
    tag1: 26,
    tag2: 26,
    tag3: 26,
    precio: 15,
    descuento: 26,
    extra: 26,
  },
  6: {
    titulo: 64,
    tag1: 26,
    tag2: 26,
    tag3: 26,
    precio: 15,
    descuento: 26,
    extra: 26,
  },
};

export function getTemplateFieldLimit(
  template: number | string | null | undefined,
  field: string
): number | null {
  const templateNum = Number(template);
  if (!templateNum || isFullBleedTemplate(templateNum)) return null;
  const limits = TEMPLATE_FIELD_LIMITS[templateNum as 1 | 2 | 3 | 6];
  if (!limits) return null;
  return limits[field] ?? null;
}

export function truncateForTemplate(
  text: string | null | undefined,
  template: number | string | null | undefined,
  field: string
): string {
  const limit = getTemplateFieldLimit(template, field);
  if (limit == null) return String(text || '');
  const str = String(text || '');
  return str.length <= limit ? str : str.slice(0, limit);
}

/** Preview slots that may reserve empty space. Hover-x4 only collects title (+ description). */
export type PreviewSlots = {
  titulo: boolean;
  tags: boolean;
  bullets: boolean;
  precio: boolean;
  icons: boolean;
};

export const FULL_PREVIEW_SLOTS: PreviewSlots = {
  titulo: true,
  tags: true,
  bullets: true,
  precio: true,
  icons: true,
};

export const HOVER_PREVIEW_SLOTS: PreviewSlots = {
  titulo: true,
  tags: false,
  bullets: false,
  precio: false,
  icons: false,
};

export function getPreviewSlots({ hoverOverlay }: { hoverOverlay?: boolean } = {}): PreviewSlots {
  return hoverOverlay ? HOVER_PREVIEW_SLOTS : FULL_PREVIEW_SLOTS;
}

export function buildTemplatePreviewModel({
  template,
  titulo,
  mostrarTitulo,
  backgroundTag,
  colorTag,
  beneficio1,
  beneficio2,
  tag1,
  tag2,
  tag3,
  mostrarTag1,
  mostrarTag2,
  mostrarTag3,
  precio,
  descuento,
  extra,
  mostrarExtra,
  mostrarPrecio,
  mostrarDescuento,
  iconos,
  colorIconos,
  descripcion,
  imageUrl,
  mobileImageUrl,
}: {
  template?: number | string | null;
  titulo?: string;
  mostrarTitulo?: boolean;
  backgroundTag?: string;
  colorTag?: string;
  beneficio1?: string;
  beneficio2?: string;
  tag1?: string;
  tag2?: string;
  tag3?: string;
  mostrarTag1?: boolean;
  mostrarTag2?: boolean;
  mostrarTag3?: boolean;
  precio?: string;
  descuento?: string;
  extra?: string;
  mostrarExtra?: boolean;
  mostrarPrecio?: boolean;
  mostrarDescuento?: boolean;
  iconos?: unknown;
  colorIconos?: string;
  descripcion?: string;
  imageUrl?: string;
  mobileImageUrl?: string;
} = {}) {
  const sharedBg = backgroundTag || DEFAULT_TAG_BG_COLOR;
  const sharedColor = colorTag || DEFAULT_TAG_TEXT_COLOR;
  const tags = buildTagsFromApiFields({
    tag1,
    tag2,
    tag3,
    mostrarTag1,
    mostrarTag2,
    mostrarTag3,
  });
  const visibleTags = tags.map((tag) => (tag && !tag.hide && tag.text ? tag : null));
  const truncatedVisibleTags = visibleTags.map((tag, index) => {
    if (!tag) return null;
    const field = `tag${index + 1}`;
    const text = truncateForTemplate(tag.text, template, field);
    if (!text) return null;
    return { ...tag, text };
  });
  const [visibleTag1, visibleTag2, visibleTag3] = truncatedVisibleTags;
  const combinedTags = [visibleTag1, visibleTag2].filter(Boolean);
  const selectedIcons = normalizeIconos(iconos);

  return {
    title: mostrarTitulo ? '' : truncateForTemplate(titulo || '', template, 'titulo'),
    tagCombined: combinedTags.length
      ? {
          text: combinedTags.map((tag) => tag.text).join(' | '),
          bg: sharedBg,
          color: sharedColor,
        }
      : null,
    tagBadge: visibleTag3 ? { text: visibleTag3.text, bg: sharedBg, color: sharedColor } : null,
    bullets: [beneficio1, beneficio2]
      .map((bullet, index) =>
        truncateForTemplate(bullet, template, index === 0 ? 'beneficio1' : 'beneficio2')
      )
      .filter(Boolean),
    precio: mostrarPrecio ? '' : truncateForTemplate(precio || '', template, 'precio'),
    descuento: mostrarDescuento ? '' : truncateForTemplate(descuento || '', template, 'descuento'),
    extra: mostrarExtra ? '' : truncateForTemplate(extra || '', template, 'extra'),
    icons: {
      color: colorIconos || DEFAULT_ICON_COLOR,
      items: selectedIcons.map((id) => ICONS.find((icon) => icon.id === id)).filter(Boolean),
    },
    imageUrl: imageUrl || '',
    mobileImageUrl: mobileImageUrl || '',
    description: descripcion || '',
  };
}

export function normalizeBannerForTemplate(data: Record<string, unknown> = {}) {
  return {
    titulo: (data.titulo as string) ?? (data.title as string) ?? '',
    descripcion: (data.descripcion as string) ?? (data.description as string) ?? '',
    precio: (data.precio as string) ?? (data.manualPrice as string) ?? '',
    descuento: (data.descuento as string) ?? '',
    extra: (data.extra as string) ?? (data.tag as string) ?? '',
    mostrarTitulo: Boolean(data.mostrarTitulo),
    mostrarDescripcion: Boolean(data.mostrarDescripcion),
    mostrarPrecio: Boolean(data.mostrarPrecio),
    mostrarDescuento: Boolean(data.mostrarDescuento),
    mostrarExtra: Boolean(data.mostrarExtra),
    tag1: data.tag1 as string | undefined,
    tag2: data.tag2 as string | undefined,
    tag3: data.tag3 as string | undefined,
    mostrarTag1: Boolean(data.mostrarTag1),
    mostrarTag2: Boolean(data.mostrarTag2),
    mostrarTag3: Boolean(data.mostrarTag3),
    colorTag: data.colorTag as string | undefined,
    backgroundTag: data.backgroundTag as string | undefined,
    beneficio1: data.beneficio1 as string | undefined,
    beneficio2: data.beneficio2 as string | undefined,
    iconos: data.iconos,
    colorIconos: data.colorIconos as string | undefined,
    imageUrl: (data.imagen as string) ?? (data.imageUrl as string) ?? '',
    imagenMobile: (data.imagenMobile as string) ?? '',
    degradeImagen: data.degradeImagen,
  };
}
